import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router";
import Navbar from "../../../../globals/components/Navbar";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  IconButton,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
  CircularProgress,
  InputAdornment,
  TableSortLabel,
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import DownloadIcon from "@mui/icons-material/Download";
import DeleteIcon from "@mui/icons-material/Delete";
import { apiClient } from "../../../../api/httpClient"; // Assuming you have apiClient setup
import { toast } from "react-toastify";
import TablePaginationActions from "../../../../globals/components/TablePaginationActions";
import { exportToExcel as writeExcelFile } from "../../../../globals/utils/excelExport";

function ProfessionalGuardianList() {
  const navigate = useNavigate();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [open, setOpen] = useState(false); // eslint-disable-line no-unused-vars
  const [email, setEmail] = useState(""); // eslint-disable-line no-unused-vars
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [guardians, setGuardians] = useState([]);
  const [exportLoading, setExportLoading] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedGuardian, setSelectedGuardian] = useState(null);
  const [orderBy, setOrderBy] = useState("name");
  const [order, setOrder] = useState("asc");

  function formatToUSDWithoutCents(amount) {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
    }).format(amount);
  }

  const handleChangePage = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  useEffect(() => {
    const fetchGuardians = async () => {
      setLoading(true);
      try {
        const response = await apiClient(
          "/get-all-professional-guardians",
          "GET"
        );
        if (response && response.data) {
          setGuardians(response.data); // Adjust based on the structure of your payload
        } else {
          console.error("Invalid response structure:", response);
        }
      } catch (error) {
        console.error("Failed to fetch guardians:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchGuardians();
  }, []);

  const filteredGuardians = guardians.filter(
    (guardian) =>
      (guardian.full_name?.toLowerCase() || "").includes(searchQuery.toLowerCase()) ||
      (guardian.preferred_name?.toLowerCase() || "").includes(searchQuery.toLowerCase())
  );

  const getSortableValue = (guardian, column) => {
    switch (column) {
      case "name":
        return (guardian.full_name || guardian.preferred_name || "").toString();
      case "email":
        return (guardian.user?.email || "").toString();
      case "oppg_number":
        return (guardian.oppg_number || "").toString();
      case "total_bonds":
        return Number(guardian.total_bonds || 0);
      case "total_outstanding_bond_amount":
        return Number(guardian.total_outstanding_bond_amount || 0);
      default:
        return "";
    }
  };

  const sortRows = (rows) => {
    return [...rows].sort((a, b) => {
      const aVal = getSortableValue(a, orderBy);
      const bVal = getSortableValue(b, orderBy);

      if (typeof aVal === "number" && typeof bVal === "number") {
        return order === "asc" ? aVal - bVal : bVal - aVal;
      }

      const aStr = aVal.toString().toLowerCase();
      const bStr = bVal.toString().toLowerCase();
      return order === "asc" ? aStr.localeCompare(bStr) : bStr.localeCompare(aStr);
    });
  };

  const handleSort = (column) => {
    const isAsc = orderBy === column && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(column);
  };

  const currentRows = sortRows(filteredGuardians).slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const handleViewClick = (guardian) => {
    navigate(`/view_professional_guardian/${guardian.pg_id}`, {
      state: { guardian },
    });
  };

  const handleDeleteClick = (guardian) => {
    setSelectedGuardian(guardian);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    setLoading(true);
    try {
      if (!selectedGuardian?.pg_id) return;
      await apiClient(
        `/professional-guardian-user/${selectedGuardian.pg_id}/`,
        "DELETE"
      );
      toast.success("Guardian deleted successfully!");
      // Refresh the guardians list
      const response = await apiClient(
        "/get-all-professional-guardians",
        "GET"
      );
      if (response && response.data) {
        setGuardians(response.data);
      }
    } catch (error) {
      toast.error("Failed to delete guardian. Please try again.");
    } finally {
      setLoading(false);
      setDeleteDialogOpen(false);
      setSelectedGuardian(null);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setSelectedGuardian(null);
  };

  const handleExportClick = async () => {
    setExportLoading(true);
    try {
      const dataToExport = guardians.map((guardian) => ({
        "Guardian ID": guardian.pg_id || "",
        "Full Name": guardian.full_name || "",
        "Preferred Name": guardian.preferred_name || "",
        "Email Address": guardian.user?.email || "N/A",
        "OPPG Number": guardian.oppg_number || "",
        "Total Bonds": guardian.total_bonds || 0,
        "Total Outstanding Bond Amount":
          guardian.total_outstanding_bond_amount || 0,
      }));

      await writeExcelFile(
        dataToExport,
        "Professional Guardians",
        "professional_guardians_export"
      );
    } catch (error) {
      console.error("Failed to export guardians:", error);
      toast.error("Failed to export data!");
    } finally {
      setExportLoading(false);
    }
  };

  return (
    <>
      <Navbar />

      {/* Main Content Wrapper */}
      <Box sx={{ maxWidth: "100%", mt: 4, px: 3 }}>
        {/* Heading */}
        <Typography variant="h4" sx={{ fontWeight: "bold", mb: 2 }}>
          Professional Guardians
        </Typography>

        {/* Search bar and Action Buttons aligned */}
        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 3 }}>
          <TextField
            label="Search by Name"
            variant="outlined"
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setPage(0); // Reset to first page when searching
            }}
            sx={{
              width: "300px",
              borderRadius: "8px",
              "& .MuiOutlinedInput-root": {
                borderRadius: "8px",
              },
            }}
            slotProps={{ input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}}
          />
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button
              variant="outlined"
              color="primary"
              startIcon={
                exportLoading ? (
                  <CircularProgress size={16} />
                ) : (
                  <DownloadIcon />
                )
              }
              onClick={handleExportClick}
              disabled={exportLoading}
            >
              Export
            </Button>
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={() => navigate("/create_professional_guardian")}
            >
              Add Professional Guardian
            </Button>
          </Box>
        </Box>
        {loading ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            height="60vh"
          >
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer
            component={Paper}
            sx={{
              width: "100%",
              overflowX: "auto",
              mb: 2,
              borderRadius: "8px",
            }}
          >
            <Table>
              <TableHead>
                <TableRow style={{ backgroundColor: "#f0f0f0" }}>
                  <TableCell sortDirection={orderBy === "name" ? order : false}>
                    <TableSortLabel
                      active={orderBy === "name"}
                      direction={orderBy === "name" ? order : "asc"}
                      onClick={() => handleSort("name")}
                    >
                      Name
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="right" sortDirection={orderBy === "email" ? order : false}>
                    <TableSortLabel
                      active={orderBy === "email"}
                      direction={orderBy === "email" ? order : "asc"}
                      onClick={() => handleSort("email")}
                    >
                      Email Address
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="right" sortDirection={orderBy === "oppg_number" ? order : false}>
                    <TableSortLabel
                      active={orderBy === "oppg_number"}
                      direction={orderBy === "oppg_number" ? order : "asc"}
                      onClick={() => handleSort("oppg_number")}
                    >
                      OPPG Number
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="right" sortDirection={orderBy === "total_bonds" ? order : false}>
                    <TableSortLabel
                      active={orderBy === "total_bonds"}
                      direction={orderBy === "total_bonds" ? order : "asc"}
                      onClick={() => handleSort("total_bonds")}
                    >
                      Total Bonds
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="right" sortDirection={orderBy === "total_outstanding_bond_amount" ? order : false}>
                    <TableSortLabel
                      active={orderBy === "total_outstanding_bond_amount"}
                      direction={orderBy === "total_outstanding_bond_amount" ? order : "asc"}
                      onClick={() => handleSort("total_outstanding_bond_amount")}
                    >
                      Total Outstanding Bond Amount
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {currentRows.map((guardian, index) => (
                  <TableRow key={index}>
                    <TableCell>
                      {guardian.full_name || guardian.preferred_name}
                    </TableCell>
                    <TableCell align="right">{guardian.user?.email || "N/A"}</TableCell>
                    <TableCell align="right">{guardian.oppg_number || "N/A"}</TableCell>
                    <TableCell align="right">{guardian.total_bonds || 0}</TableCell>
                    <TableCell align="right">
                      {formatToUSDWithoutCents(
                        guardian.total_outstanding_bond_amount || 0
                      )}
                    </TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: "flex", justifyContent: "center" }}>
                        <IconButton
                          onClick={() => handleViewClick(guardian)}
                          color="primary"
                        >
                          <VisibilityIcon />
                        </IconButton>
                        <IconButton
                          onClick={() => handleDeleteClick(guardian)}
                          color="error"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        <TablePagination
          ActionsComponent={TablePaginationActions}
          component="div"
          count={filteredGuardians.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 25]}
        />
      </Box>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={handleDeleteCancel}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete {selectedGuardian?.full_name || selectedGuardian?.preferred_name || "this guardian"}? This
            action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} color="primary">
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default ProfessionalGuardianList;
